/**
 * Version Upload Store
 *
 * Zustand store for managing version upload state in the preview panel.
 * Persists upload state to sessionStorage to survive panel close/reopen.
 *
 * Features:
 * - Tracks upload state per table (supports multiple concurrent uploads)
 * - SessionStorage persistence for state recovery
 * - Auto-clears completed/failed uploads
 *
 * @module features/workspace/stores/useVersionUploadStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { VersionStatus } from '@loader/shared-types';
import { VERSION_IN_PROGRESS_STATUSES } from '@loader/shared-types';

import { MAX_PROCESSING_STALENESS_MS } from '@/lib/constants';
import type { UploadVisualState } from '@/lib/uploadTypes';

// ============================================================================
// CONSTANTS
// ============================================================================

/** SessionStorage key for persisting version upload state */
const STORAGE_KEY = 'version-upload-storage';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Upload state for a single table
 */
export interface TableUploadState {
  /** Version ID being uploaded/processed */
  versionId: string;
  /** Current visual state of the upload */
  visualState: UploadVisualState;
  /** Version status from backend (for recovery) */
  versionStatus: VersionStatus | null;
  /** Timestamp when upload started (for cleanup of stale entries) */
  startedAt: number;
  /** Last-known progress value for recovery (0-100, mapped to combined range) */
  progress: number;
}

/**
 * Store state
 */
interface VersionUploadStoreState {
  /** Upload state keyed by tableId */
  uploads: Record<string, TableUploadState>;
}

/**
 * Store actions
 */
interface VersionUploadStoreActions {
  /** Set upload state for a table */
  setUpload: (tableId: string, state: Omit<TableUploadState, 'startedAt' | 'progress'>) => void;
  /** Update version status for a table (from polling) */
  updateVersionStatus: (tableId: string, status: VersionStatus) => void;
  /** Update visual state for a table */
  updateVisualState: (tableId: string, visualState: UploadVisualState) => void;
  /** Update progress for a table (for recovery) */
  updateProgress: (tableId: string, progress: number) => void;
  /** Clear upload state for a table */
  clearUpload: (tableId: string) => void;
  /** Get upload state for a table */
  getUpload: (tableId: string) => TableUploadState | null;
  /** Check if a table has an in-progress upload */
  hasInProgressUpload: (tableId: string) => boolean;
}

type VersionUploadStore = VersionUploadStoreState & VersionUploadStoreActions;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Check if an upload state represents an in-progress upload (not stale).
 * Returns false if the upload is older than MAX_PROCESSING_STALENESS_MS,
 * preventing the UI from getting stuck showing a loading state for stuck versions.
 */
function isInProgress(state: TableUploadState | null): boolean {
  if (!state) return false;

  // Check staleness first - stale uploads are never "in progress"
  if (Date.now() - state.startedAt > MAX_PROCESSING_STALENESS_MS) {
    return false;
  }

  // Check visual state
  if (state.visualState === 'uploading' || state.visualState === 'processing') {
    return true;
  }

  // Also check version status if available
  if (state.versionStatus && VERSION_IN_PROGRESS_STATUSES.includes(state.versionStatus)) {
    return true;
  }

  return false;
}

/**
 * Clean up stale entries (older than threshold)
 */
function cleanupStaleEntries(uploads: Record<string, TableUploadState>): Record<string, TableUploadState> {
  const cleaned: Record<string, TableUploadState> = {};

  for (const [tableId, state] of Object.entries(uploads)) {
    // isInProgress already includes staleness check
    if (isInProgress(state)) {
      cleaned[tableId] = state;
    }
  }

  return cleaned;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * Version upload store
 *
 * Manages upload state for version uploads in the preview panel.
 * State is persisted to sessionStorage to survive panel close/reopen.
 */
export const useVersionUploadStore = create<VersionUploadStore>()(
  persist(
    (set, get) => ({
      uploads: {},

      setUpload: (tableId, state) => {
        set((prev) => ({
          uploads: {
            ...prev.uploads,
            [tableId]: {
              ...state,
              startedAt: Date.now(),
              progress: 0,
            },
          },
        }));
      },

      updateVersionStatus: (tableId, status) => {
        set((prev) => {
          const existing = prev.uploads[tableId];
          if (!existing) return prev;

          return {
            uploads: {
              ...prev.uploads,
              [tableId]: {
                ...existing,
                versionStatus: status,
              },
            },
          };
        });
      },

      updateVisualState: (tableId, visualState) => {
        set((prev) => {
          const existing = prev.uploads[tableId];
          if (!existing) return prev;

          return {
            uploads: {
              ...prev.uploads,
              [tableId]: {
                ...existing,
                visualState,
              },
            },
          };
        });
      },

      updateProgress: (tableId, progress) => {
        set((prev) => {
          const existing = prev.uploads[tableId];
          if (!existing) return prev;

          return {
            uploads: {
              ...prev.uploads,
              [tableId]: {
                ...existing,
                progress,
              },
            },
          };
        });
      },

      clearUpload: (tableId) => {
        set((prev) => {
          const { [tableId]: _, ...rest } = prev.uploads;
          return { uploads: rest };
        });
      },

      getUpload: (tableId) => {
        return get().uploads[tableId] ?? null;
      },

      hasInProgressUpload: (tableId) => {
        const state = get().uploads[tableId];
        return isInProgress(state);
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      // Clean up stale entries on rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.uploads = cleanupStaleEntries(state.uploads);
        }
      },
      // Only persist uploads that are in progress
      partialize: (state) => ({
        uploads: Object.fromEntries(
          Object.entries(state.uploads).filter(([, upload]) => isInProgress(upload))
        ),
      }),
    }
  )
);

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

/**
 * Hook to get upload state for a specific table
 */
export function useTableUploadState(tableId: string): TableUploadState | null {
  return useVersionUploadStore((state) => state.uploads[tableId] ?? null);
}

/**
 * Hook to check if a table has an in-progress upload
 *
 * Use this to disable actions that shouldn't occur during uploads,
 * such as deleting a table or starting another upload.
 */
export function useHasInProgressUpload(tableId: string): boolean {
  return useVersionUploadStore((state) => {
    const upload = state.uploads[tableId];
    return isInProgress(upload);
  });
}
